/**
 * The metric overlay: HMI-style widgets burned into the broadcast.
 *
 * Two things live here, and they are kept together on purpose: the widget shape
 * an operator authors in the Designer, and the pure helpers that turn a widget
 * into pixels. The filter compiler (`overlayFilters.ts`), the value writer
 * (`main/metrics.ts`) and the Designer canvas all import these — if any of them
 * computed geometry or slot selection on its own, WYSIWYG would quietly stop
 * being true, and the burned-in frame would disagree with the one the operator
 * designed against.
 *
 * Like the rest of `shared/`, this module touches neither the filesystem nor
 * Electron: it is imported by the renderer.
 */

/**
 * What a widget draws.
 *
 * Deliberately small. Every type here has to compile to drawbox/drawtext, and
 * that rules out whole families of HMI widget — trends and dial gauges need
 * per-frame vector drawing, and images need their own ffmpeg input, which would
 * shift every filter label and the audio map. The Designer palette shows those
 * greyed with the reason rather than pretending they are coming.
 */
export type OverlayWidgetType = 'value' | 'label' | 'bar' | 'lamp' | 'trend' | 'symbol'

export type OverlayAlign = 'left' | 'center' | 'right'
export type OverlayVAlign = 'top' | 'middle' | 'bottom'

/** How a lamp turns its reading into a yes or a no. */
export type LampCompare = 'gte' | 'lte' | 'eq' | 'neq'

/**
 * How a symbol's image meets its box: kept whole on a transparent bed,
 * cropped to fill, or stretched to the exact box.
 */
export type SymbolFit = 'contain' | 'cover' | 'stretch'

/**
 * One value->text mapping on a Value widget: "0 reads STOP in the critical
 * colour, 1 reads RUNNING". Severity picks which of the widget's existing
 * colour slots draws the text — reusing the normal/warn/crit passes the graph
 * already has, so a mapped state costs no extra filter and updates on air.
 */
export interface ValueCondition {
  compare: LampCompare
  threshold: number
  /** Shown instead of the number; empty falls back to the formatted value. */
  text: string
  /** 'custom' draws in this condition's own colour (an extra filter pass). */
  severity: 'normal' | 'warn' | 'crit' | 'custom'
  /** Only read when severity is 'custom'. */
  color: string
  /**
   * Design-space position the widget moves to while this condition holds;
   * null leaves that axis at the widget's own place. Text widgets move via a
   * dedicated drawtext at the target (the slot machinery — see activeSlots);
   * symbols move via the composed image (see symbolBounds).
   */
  x: number | null
  y: number | null
}

/** Whether this condition carries the widget somewhere else while it holds. */
export function conditionMoved(c: ValueCondition): boolean {
  return c.x !== null || c.y !== null
}

/** Where the widget's box sits while a condition holds (design space). */
export function conditionPlace(widget: OverlayWidget, c: ValueCondition): { x: number; y: number } {
  return { x: c.x ?? widget.x, y: c.y ?? widget.y }
}

export const MAX_VALUE_CONDITIONS = 8

/**
 * One drawtext instance per widget, distinguished by the colour it draws in.
 *
 * ffmpeg cannot change a filter's `fontcolor` from file contents, so a value
 * that must turn amber is drawn by N stacked drawtexts at the same anchor, of
 * which the writer fills exactly one and blanks the rest. `lbl` is the widget's
 * caption, which never changes colour.
 */
export type OverlaySlot = 'lbl' | 'n' | 'w' | 'c' | 's' | 'on' | 'off' | `x${number}`

export interface OverlayThresholds {
  warnHigh: number | null
  critHigh: number | null
  warnLow: number | null
  critLow: number | null
}

export interface OverlayWidget {
  /**
   * Also used as a filename, so it is restricted to [A-Za-z0-9_-] on the way in
   * (see `normaliseWidget` in main/settings.ts).
   */
  id: string
  type: OverlayWidgetType
  enabled: boolean
  /** Redis key, verbatim — `metric:{namespace}:{sensor}`. Empty for a label. */
  tag: string
  /** Design-space geometry (see `overlayRect`), not output pixels. */
  x: number
  y: number
  w: number
  h: number
  /** Paint order. Ascending = later in the chain = drawn on top. */
  z: number
  label: string
  unit: string
  decimals: number
  /** Design-space px; scaled per output group unless scaleMode is 'fixed'. */
  fontSize: number
  labelSize: number
  align: OverlayAlign
  valign: OverlayVAlign
  /** '#RRGGBB'. Converted to ffmpeg's 0xRRGGBB by `ffColor`. */
  color: string
  labelColor: string
  warnColor: string
  critColor: string
  staleColor: string
  showLabel: boolean
  showStale: boolean
  panel: boolean
  panelColor: string
  panelOpacity: number
  thresholds: OverlayThresholds
  /** Bar only: the value range the cells span. */
  min: number
  max: number
  barCells: number
  barGlyph: 'block' | 'ascii'

  /** Value and Bar only: ordered value->text mappings; first match wins. */
  conditions: ValueCondition[]

  /** Lamp only: the test, and what each outcome looks like. */
  compare: LampCompare
  threshold: number
  textOn: string
  textOff: string
  colorOn: string
  colorOff: string
  /** A filled circle ahead of the text, which is what makes it read as a lamp. */
  lampGlyph: 'dot' | 'none'

  /**
   * Symbol only: image shown when no condition matches (empty = transparent).
   * Conditions map value -> image via their `text` field holding a file path.
   */
  symbolDefault: string
  /** Symbol only: how the image meets the widget box. */
  symbolFit: SymbolFit

  /** Trend only: how many recent samples to draw, and whether to fix the scale. */
  trendCells: number
  trendGlyph: 'bars' | 'ascii'
  /** Scale each frame to the window's own range rather than to min/max. */
  trendAuto: boolean
}

export interface OverlayConfig {
  enabled: boolean
  /** The canvas the operator designs against; geometry is relative to this. */
  designWidth: number
  designHeight: number
  /**
   * 'design' scales fonts with the output; 'fixed' holds them at design px.
   *
   * Geometry always scales — a widget that kept its pixel position would fall
   * off a smaller canvas entirely.
   */
  scaleMode: 'design' | 'fixed'
  /** 'all' = the wall and every single-camera stream; 'wall' = the wall only. */
  applyTo: 'all' | 'wall'
  /** How often drawtext re-reads the value files, in Hz. */
  reloadHz: number
  /**
   * How many spare object slots the graph carries for live editing.
   *
   * A filter cannot be added to a running ffmpeg, so adding an object on air is
   * only possible if the graph already holds an unused one to move into place.
   * Each spare costs a handful of idle drawtext passes on every frame — metered
   * at roughly 4% of encoder headroom per spare on a 1080p30 wall — so this is
   * a budget the operator sets, not a constant. Defaults to 0: adding an object
   * on air is not yet verified end to end on a broadcast, and an unproven
   * feature must not spend a production box's headroom by default.
   */
  liveSpares: number
  /** Empty falls back to labelFont()/monoFont() for the host platform. */
  fontPath: string
  monoFontPath: string
  widgets: OverlayWidget[]
}

/**
 * Where the metric values come from.
 *
 * Not part of StreamConfig: this is per-site infrastructure carrying a
 * password, and StreamConfig is pushed down from the fleet console and reported
 * back up on every heartbeat. It is resolved from a local ini file and the
 * environment instead — see main/metricsSource.ts, and the same reasoning
 * already written down in main/cloudStore.ts.
 */
export interface MetricsSource {
  host: string
  port: number
  /** Masked to '••••••••' before this ever leaves the main process. */
  password: string
  db: number
  pollMs: number
  staleSec: number
  scanMatch: string
  /** Which layer won, so the UI can answer "why is it using that?". */
  origin: 'default' | 'file' | 'env'
  filePath: string
}

/** State of the metric feed, pushed to the UI so a dead edge box is visible. */
export interface MetricsState {
  connected: boolean
  lastError: string | null
  tagCount: number
}

/** One tag's latest value, as handed to the Designer. */
export interface MetricSample {
  value: string
  at: number
  stale: boolean
  /** Recent samples, newest first, for trend widgets. Absent when unused. */
  history?: string[]
}

/**
 * Filter-graph cost is widgets x colour slots x output groups, and each drawtext
 * loads its own font face. This is the ceiling before that becomes the reason a
 * stream drops frames.
 */
export const MAX_OVERLAY_WIDGETS = 24

/** Upper bound on spares, so a slip of the keyboard cannot bury the encoder. */
export const MAX_LIVE_SPARES = 8

/**
 * The slots a spare declares, and therefore the shapes it can host.
 *
 * Fixed at graph build because `textfile` is NOT a runtime option — ffmpeg
 * refuses to re-point a drawtext at another file once it is initialised. That
 * single fact decides the whole design: a spare cannot be handed a widget's
 * files, so the widget must adopt the SPARE'S identity instead, and the writer
 * then fills the spare's files as it would any other widget's. Conditions are
 * excluded (each one wants its own x-slot at its own anchor); a widget that
 * uses them still needs a restart.
 */
export const SPARE_SLOTS: OverlaySlot[] = ['lbl', 'n', 'w', 'c', 's', 'on', 'off']

/** Reserved ids for spares. Never handed out by the palette's own counter. */
export function spareId(index: number): string {
  return `__s${index + 1}`
}

export function isSpareId(id: string): boolean {
  return /^__s\d+$/.test(id)
}

/** Spare ids the graph declares but no widget currently occupies. */
export function freeSpareIds(overlay: OverlayConfig): string[] {
  const used = new Set(overlay.widgets.map((w) => w.id))
  const count = Math.min(MAX_LIVE_SPARES, Math.max(0, Math.round(overlay.liveSpares ?? 0)))
  return Array.from({ length: count }, (_, i) => spareId(i)).filter((id) => !used.has(id))
}

export const DEFAULT_THRESHOLDS: OverlayThresholds = {
  warnHigh: null,
  critHigh: null,
  warnLow: null,
  critLow: null
}

export const DEFAULT_WIDGET: OverlayWidget = {
  id: 'w1',
  type: 'value',
  enabled: true,
  tag: '',
  x: 64,
  y: 64,
  w: 320,
  h: 120,
  z: 1,
  label: '',
  unit: '',
  decimals: 1,
  fontSize: 54,
  labelSize: 22,
  align: 'left',
  valign: 'middle',
  color: '#E6EDF3',
  labelColor: '#8B949E',
  warnColor: '#D29922',
  critColor: '#F85149',
  staleColor: '#6E7681',
  showLabel: true,
  showStale: true,
  panel: true,
  panelColor: '#000000',
  panelOpacity: 0.45,
  thresholds: { ...DEFAULT_THRESHOLDS },
  min: 0,
  max: 100,
  barCells: 12,
  barGlyph: 'block',
  compare: 'gte',
  conditions: [],
  threshold: 1,
  textOn: 'RUNNING',
  textOff: 'STOPPED',
  colorOn: '#3FB950',
  colorOff: '#8B949E',
  lampGlyph: 'dot',
  symbolDefault: '',
  symbolFit: 'contain',
  trendCells: 24,
  trendGlyph: 'bars',
  trendAuto: true
}

export const DEFAULT_OVERLAY: OverlayConfig = {
  enabled: false,
  designWidth: 1920,
  designHeight: 1080,
  scaleMode: 'design',
  applyTo: 'all',
  reloadHz: 2,
  liveSpares: 0,
  fontPath: '',
  monoFontPath: '',
  widgets: []
}

/** A new widget of the given type, with the per-type defaults applied. */
export function blankWidget(
  id: string,
  type: OverlayWidgetType,
  patch: Partial<OverlayWidget> = {}
): OverlayWidget {
  const base: OverlayWidget = { ...DEFAULT_WIDGET, id, type }
  if (type === 'label') {
    base.label = 'Label'
    base.showLabel = false
    base.showStale = false
    base.fontSize = 32
    base.h = 56
    base.w = 240
  }
  if (type === 'bar') {
    base.fontSize = 34
    base.h = 100
    base.w = 360
  }
  if (type === 'lamp') {
    base.label = 'CONVEYOR'
    base.fontSize = 40
    base.h = 110
    base.w = 340
    // A lamp reads as a state, not a measurement, so it has no thresholds and
    // its own two colours carry the meaning instead.
    base.showStale = true
  }
  return { ...base, ...patch }
}

/** Widget ids double as filenames; keep them to characters that need no quoting. */
export function safeWidgetId(id: string, fallback: string): string {
  const clean = (id ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40)
  return clean || fallback
}

/**
 * Where one slot's text lives.
 *
 * Joined with a forward slash rather than path.join because this module is
 * imported by the renderer and cannot use node:path. Windows accepts forward
 * slashes in both Node's fs and ffmpeg's file arguments, so the writer and the
 * filter string agree on one spelling — which is the point, since they must
 * name the identical file.
 */
export function overlaySlotFile(dir: string, widgetId: string, slot: OverlaySlot): string {
  return `${dir.replace(/\\/g, '/').replace(/\/+$/, '')}/${widgetId}.${slot}.txt`
}

/**
 * The slots this widget actually draws.
 *
 * Adaptive rather than always-four: an unthresholded readout needs one colour,
 * and emitting three more drawtexts that can never be filled would triple its
 * cost for nothing. The compiler emits exactly these, and the writer maintains
 * exactly these — sharing the function is what stops the two drifting into a
 * graph that references a file nobody writes.
 */
export function activeSlots(widget: OverlayWidget): OverlaySlot[] {
  // A symbol has no text to draw — its state lives in the swapped image file.
  if (widget.type === 'symbol') return []
  const slots: OverlaySlot[] = []
  if (widget.showLabel) slots.push('lbl')

  if (widget.type === 'lamp') {
    // Two states rather than a colour ramp: a lamp is on or it is off, and the
    // 'n'/'w'/'c' bands would be an unused drawing pass on every output.
    slots.push('on', 'off')
    if (widget.showStale) slots.push('s')
    return slots
  }

  // Trends share the value/bar slot logic below: bands and conditions judge
  // the latest reading and recolour (or replace) the whole sparkline.
  slots.push('n')
  const t = widget.thresholds
  // A slot exists when EITHER a numeric band or a mapped condition can select
  // it — a condition writing into a slot the graph never drew would blank the
  // widget the moment it matched.
  // A moved condition always gets its own x-slot (the shared w/c anchors sit at
  // the widget's home position), so it never demands the shared slots.
  const sev = new Set(widget.conditions.filter((c) => !conditionMoved(c)).map((c) => c.severity))
  if (t.warnHigh !== null || t.warnLow !== null || sev.has('warn')) slots.push('w')
  if (t.critHigh !== null || t.critLow !== null || sev.has('crit')) slots.push('c')
  widget.conditions.forEach((c, i) => {
    if (c.severity === 'custom' || conditionMoved(c)) slots.push(`x${i}`)
  })
  if (widget.showStale) slots.push('s')
  return slots
}

/** The colour a slot draws in. */
export function slotColor(widget: OverlayWidget, slot: OverlaySlot): string {
  if (slot.startsWith('x')) {
    // An x-slot exists for a custom colour OR a moved condition; a moved
    // warn/crit still draws in the widget's band colours.
    const c = widget.conditions[Number.parseInt(slot.slice(1), 10)]
    if (!c) return widget.color
    if (c.severity === 'custom') return c.color || widget.color
    if (c.severity === 'crit') return widget.critColor
    if (c.severity === 'warn') return widget.warnColor
    return widget.color
  }
  switch (slot) {
    case 'lbl':
      return widget.labelColor
    case 'w':
      return widget.warnColor
    case 'c':
      return widget.critColor
    case 's':
      return widget.staleColor
    case 'on':
      return widget.colorOn
    case 'off':
      return widget.colorOff
    default:
      return widget.color
  }
}

/**
 * Read a metric as a truth value.
 *
 * The string coercion is not incidental. SmartLinkOS stores whatever the source
 * wrote: a Modbus coil arrives as `1`, but `redis_client.set_metric` will also
 * have written `true`/`false` for a boolean sensor, and its own `get_metric`
 * coerces those to 1.0/0.0 on the way out. A lamp bound to such a key would
 * otherwise read NaN and sit permanently off — looking exactly like a stopped
 * machine.
 */
function numericValue(raw: string | null): number {
  if (raw === null) return NaN
  const clean = sanitizeValue(raw).toLowerCase()
  if (clean === 'true' || clean === 'on' || clean === 'yes') return 1
  if (clean === 'false' || clean === 'off' || clean === 'no') return 0
  return Number.parseFloat(clean)
}

/**
 * Whether a lamp is lit.
 *
 * Shared by the value writer and the Designer's preview on purpose — two
 * implementations of this test would eventually disagree, and the operator
 * would be designing against a lamp that behaves differently on air.
 */
/** First matching condition on a Value widget, or null. */
export function matchedCondition(
  raw: string | null,
  widget: OverlayWidget
): ValueCondition | null {
  if (widget.type === 'label' || widget.type === 'lamp' || widget.conditions.length === 0) {
    return null
  }
  // Symbols map value -> image; the same first-match rules apply.
  const n = numericValue(raw)
  if (!Number.isFinite(n)) return null
  for (const c of widget.conditions) {
    const hit =
      c.compare === 'lte' ? n <= c.threshold
      : c.compare === 'eq' ? n === c.threshold
      : c.compare === 'neq' ? n !== c.threshold
      : n >= c.threshold
    if (hit) return c
  }
  return null
}

export function lampOn(raw: string | null, widget: OverlayWidget): boolean {
  const n = numericValue(raw)
  if (!Number.isFinite(n)) return false
  switch (widget.compare) {
    case 'lte':
      return n <= widget.threshold
    case 'eq':
      return n === widget.threshold
    case 'neq':
      return n !== widget.threshold
    default:
      return n >= widget.threshold
  }
}

/**
 * Which slot carries the value right now.
 *
 * Only ever returns a slot that `activeSlots` emits: a stale reading on a
 * widget with `showStale` off falls back to the normal colour rather than
 * vanishing into a file the graph never reads.
 */
export function slotFor(raw: string | null, widget: OverlayWidget, stale: boolean): OverlaySlot {
  if (widget.type === 'lamp') {
    if (stale && widget.showStale) return 's'
    return lampOn(raw, widget) ? 'on' : 'off'
  }
  if (stale) return widget.showStale ? 's' : 'n'
  // A mapped state names its own severity; the numeric bands only apply when
  // no condition matched.
  const mapped = matchedCondition(raw, widget)
  if (mapped) {
    if (mapped.severity === 'custom' || conditionMoved(mapped)) {
      return `x${widget.conditions.indexOf(mapped)}`
    }
    return mapped.severity === 'crit' ? 'c' : mapped.severity === 'warn' ? 'w' : 'n'
  }
  const t = widget.thresholds
  const n = raw === null ? NaN : Number.parseFloat(raw)
  if (!Number.isFinite(n)) return 'n'
  const crit = (t.critHigh !== null && n >= t.critHigh) || (t.critLow !== null && n <= t.critLow)
  if (crit && (t.critHigh !== null || t.critLow !== null)) return 'c'
  const warn = (t.warnHigh !== null && n >= t.warnHigh) || (t.warnLow !== null && n <= t.warnLow)
  if (warn) return 'w'
  return 'n'
}

/**
 * Make a Redis value safe to put in a textfile.
 *
 * Newlines matter more than they look: drawtext renders one as a second line,
 * so a stray one silently pushes the layout apart rather than failing. Values
 * are arbitrary strings written by whatever wrote the key.
 */
export function sanitizeValue(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 24)
}

/** Numeric values are fixed to `decimals`; anything else passes through as text. */
export function formatValue(raw: string | null, decimals: number): string {
  if (raw === null) return '--'
  const clean = sanitizeValue(raw)
  const n = Number.parseFloat(clean)
  if (!Number.isFinite(n)) return clean
  const places = Math.min(6, Math.max(0, Math.round(decimals)))
  return n.toFixed(places)
}

/** The full text a widget's value slot carries, unit included. */
/**
 * The widget as it should actually draw on this host.
 *
 * Bars and trends are runs of Unicode block glyphs (▁▂▃▄▅▆▇█, ░), and the
 * stock Windows monospace (Consolas) is missing most of them — on a real
 * broadcast they came out as rows of tofu boxes. Until the operator points
 * monoFontPath at a face that has them (Cascadia Mono, DejaVu Sans Mono),
 * Windows falls back to the ASCII ramp, which every font since the 1980s can
 * draw. macOS keeps the pretty glyphs: Menlo covers the whole ramp — verified
 * by rendering, not by reading a spec sheet.
 */
export function effectiveWidget(
  widget: OverlayWidget,
  monoFontPath: string,
  platform: string
): OverlayWidget {
  if (platform !== 'win32' || monoFontPath.trim()) return widget
  if (widget.barGlyph !== 'ascii' || widget.trendGlyph !== 'ascii') {
    return { ...widget, barGlyph: 'ascii', trendGlyph: 'ascii' }
  }
  return widget
}

export function widgetText(
  raw: string | null,
  widget: OverlayWidget,
  history?: string[]
): string {
  if (widget.type === 'label') return sanitizeValue(widget.label)
  if (widget.type === 'symbol') {
    const src = symbolSource(raw, widget)
    return src ? (src.split(/[\\/]/).pop() ?? 'symbol') : '(no image)'
  }
  if (widget.type === 'bar') {
    // A mapped state replaces the gauge outright — a stopped pump reads
    // "STOP", not an empty bar. Empty text keeps the bar and only recolours.
    const mapped = matchedCondition(raw, widget)
    if (mapped && mapped.text.trim()) return sanitizeValue(mapped.text)
    return barText(raw, widget)
  }
  if (widget.type === 'lamp') return lampText(raw, widget)
  if (widget.type === 'trend') {
    // Judged on the LATEST reading: a matched state replaces the sparkline
    // with its text; empty text keeps the line and only recolours it.
    const mapped = matchedCondition(raw, widget)
    if (mapped && mapped.text.trim()) return sanitizeValue(mapped.text)
    return trendText(history, widget)
  }
  const mapped = matchedCondition(raw, widget)
  if (mapped && mapped.text.trim()) return sanitizeValue(mapped.text)
  const value = formatValue(raw, widget.decimals)
  const unit = sanitizeValue(widget.unit)
  return unit ? `${value} ${unit}` : value
}

/**
 * A lamp's caption for its current state.
 *
 * Decided by the same `lampOn` the slot selection uses, so the text and the
 * colour can never describe different states.
 */
export function lampText(raw: string | null, widget: OverlayWidget): string {
  const on = lampOn(raw, widget)
  const word = sanitizeValue(on ? widget.textOn : widget.textOff)
  // U+25CF, in the ordinary text face. The 'none' setting exists because a
  // font without the glyph would draw a blank box rather than nothing.
  return widget.lampGlyph === 'dot' ? `● ${word}`.trim() : word
}

/**
 * A bar drawn as text.
 *
 * drawbox's width takes an expression over frame variables, not file contents,
 * so there is no way to make a real rectangle track a value without adding
 * another ffmpeg input. Block glyphs in a monospaced face are the honest
 * alternative — the cell count, not the widget's width, is what sets how long
 * the bar renders.
 */
export function barText(raw: string | null, widget: OverlayWidget): string {
  const cells = Math.min(64, Math.max(1, Math.round(widget.barCells)))
  const filled = widget.barGlyph === 'ascii' ? '#' : '\u2588'
  const empty = widget.barGlyph === 'ascii' ? '-' : '\u2591'
  const n = raw === null ? NaN : Number.parseFloat(sanitizeValue(raw))
  if (!Number.isFinite(n)) return empty.repeat(cells)
  const span = widget.max - widget.min
  const ratio = span === 0 ? 0 : (n - widget.min) / span
  const on = Math.round(Math.min(1, Math.max(0, ratio)) * cells)
  return filled.repeat(on) + empty.repeat(cells - on)
}

/** Where a widget lands on one output, and how big its text is there. */
export interface OverlayPlacement {
  x: number
  y: number
  w: number
  h: number
  scale: number
  fontPx: number
  labelPx: number
}

/**
 * Map design-space geometry onto a concrete output size.
 *
 * Uniform scale, letterbox-centred: a widget must not squash when the same
 * design is rendered onto a differently-shaped single-camera stream, and its
 * position relative to the picture should survive the aspect change.
 *
 * Fonts scale with the geometry by default. That departs from the fixed-pixel
 * rule the audio HUD follows, and the difference is the point — that badge is
 * sized by the app, where scaling means picking a size that is wrong at both
 * 720p and 4K, while this one is sized by the operator against a stated canvas
 * and shown WYSIWYG. Holding the font while the panel scaled would draw text
 * outside its own box on any smaller output.
 */
export function overlayRect(
  widget: OverlayWidget,
  overlay: OverlayConfig,
  outWidth: number,
  outHeight: number
): OverlayPlacement {
  const dw = Math.max(1, overlay.designWidth)
  const dh = Math.max(1, overlay.designHeight)
  const scale = Math.min(outWidth / dw, outHeight / dh)
  const ox = Math.round((outWidth - dw * scale) / 2)
  const oy = Math.round((outHeight - dh * scale) / 2)
  const fontScale = overlay.scaleMode === 'fixed' ? 1 : scale
  return {
    x: ox + Math.round(widget.x * scale),
    y: oy + Math.round(widget.y * scale),
    w: Math.max(1, Math.round(widget.w * scale)),
    h: Math.max(1, Math.round(widget.h * scale)),
    scale,
    // Below ~10px drawtext turns to mush; rounding keeps it from jittering
    // between frames on a fractional scale.
    fontPx: Math.max(10, Math.round(widget.fontSize * fontScale)),
    labelPx: Math.max(9, Math.round(widget.labelSize * fontScale))
  }
}

/** '#RRGGBB' (or a bare name) to the 0xRRGGBB spelling ffmpeg's parser wants. */
export function ffColor(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((hex ?? '').trim())
  return m ? `0x${m[1].toUpperCase()}` : 'white'
}

/**
 * Escape a path for use inside a filter argument.
 *
 * The Windows drive-letter colon is the case that matters: unescaped, ffmpeg's
 * option parser reads `C:` as the end of the value. Note this is *not*
 * `safeLabel` from ffmpegArgs — that one strips characters rather than escaping
 * them, which is fine for a camera name and would quietly eat a path.
 */
export function drawtextEscape(path: string): string {
  return path.replace(/\\/g, '/').replace(/:/g, '\\:').replace(/'/g, "\\'")
}

/**
 * Font used for anything drawn onto the picture, per host platform.
 *
 * Returned RAW — no filter-parser escaping. Every consumer must run it through
 * `drawtextEscape` exactly once at the point of use. It used to come
 * pre-escaped (`C\:` on Windows), and the overlay path then escaped it again,
 * turning the drive letter into `C/\:` — a path that exists nowhere. ffmpeg
 * could not load it, fell back to a fontconfig lookup, and on stock Windows
 * (no fontconfig config) that walked straight into an access violation:
 * exit 0xC0000005 in a loop, the whole stream down. macOS never showed it
 * because its font paths carry no colon or backslash, so double-escaping was
 * the identity — exactly the kind of bug that only runs into trouble on the
 * platform it ships to.
 *
 * A path that does not exist is not always harmless even without the crash:
 * builds linked against fontconfig quietly substitute a default, but builds
 * without it fail the whole filter graph.
 */
export function platformFont(platform: string): string {
  // Tahoma over Arial on both desktop platforms: this system labels Thai
  // irrigation sites, and Arial has no Thai glyphs on EITHER platform — a
  // Thai label came out as tofu boxes on air. Tahoma ships stock on Windows
  // and in macOS Supplemental, covers Thai + Latin, and was verified by
  // rendering the actual site name through ffmpeg. DejaVuSans on Linux still
  // lacks Thai; a Thai daemon deployment should set overlay.fontPath to a
  // Noto/TLWG face.
  if (platform === 'darwin') return '/System/Library/Fonts/Supplemental/Tahoma.ttf'
  if (platform === 'linux') return '/usr/share/fonts/truetype/dejavu/DejaVuSans.ttf'
  return 'C:/Windows/Fonts/tahoma.ttf'
}

/**
 * Monospaced face, used for bar gauges.
 *
 * A bar is drawn as a run of block glyphs, so the cells only line up if every
 * glyph has the same advance — in a proportional face a half-full bar is a
 * different length depending on which characters happen to be filled.
 */
export function platformMonoFont(platform: string): string {
  if (platform === 'darwin') return '/System/Library/Fonts/Menlo.ttc'
  if (platform === 'linux') return '/usr/share/fonts/truetype/dejavu/DejaVuSansMono.ttf'
  // Raw, like platformFont above — drawtextEscape happens at the point of use.
  return 'C:/Windows/Fonts/consola.ttf'
}

/** Every distinct Redis key the enabled widgets are bound to. */
export function boundTags(overlay: OverlayConfig): string[] {
  const tags = new Set<string>()
  for (const w of overlay.widgets) {
    if (!w.enabled || w.type === 'label') continue
    const tag = w.tag.trim()
    if (tag) tags.add(tag)
  }
  return [...tags]
}

/** Widgets that will actually be drawn, in paint order. */
/**
 * Symbol widgets are their own pipeline: each is an extra concat-demuxer image
 * input whose file the value writer swaps on state changes — proven the only
 * reload path ffmpeg offers (image2 -loop caches; concat re-opens per entry).
 * They are therefore excluded from the drawtext compiler and handled by the
 * arg builder directly.
 */
export function symbolWidgets(overlay: OverlayConfig): OverlayWidget[] {
  return overlay.widgets
    .filter((w) => w.enabled && w.type === 'symbol')
    .slice(0, MAX_OVERLAY_WIDGETS)
    .sort((a, b) => a.z - b.z)
}

/**
 * Filter steps that fit an image into a w×h box, per fit mode.
 *
 * Shared by the live graph (per output group, in output pixels) and the
 * writer's compose step for moved symbols (in design pixels) — one function so
 * the two can never crop differently. The pad is transparent, which is why the
 * caller must have converted to rgba first.
 */
export function symbolFitSteps(w: number, h: number, fit: SymbolFit): string[] {
  if (fit === 'cover') {
    return [`scale=${w}:${h}:force_original_aspect_ratio=increase`, `crop=${w}:${h}`]
  }
  if (fit === 'contain') {
    return [
      `scale=${w}:${h}:force_original_aspect_ratio=decrease`,
      `pad=${w}:${h}:-1:-1:color=black@0`
    ]
  }
  return [`scale=${w}:${h}`]
}

/** The two files a symbol widget owns inside the overlay dir. */
export function symbolFile(dir: string, widgetId: string, ext: 'png' | 'txt'): string {
  return `${dir.replace(/\\/g, '/').replace(/\/+$/, '')}/${widgetId}.symbol.${ext}`
}

/** The image a symbol widget should currently show, or null for transparent. */
export function symbolSource(raw: string | null, widget: OverlayWidget): string | null {
  const mapped = matchedCondition(raw, widget)
  const path = (mapped?.text ?? '').trim() || widget.symbolDefault.trim()
  return path || null
}

/**
 * The design-space box a symbol's overlay input must cover.
 *
 * Null when no condition moves the widget — the input then covers exactly the
 * widget box and the writer copies images verbatim (the cheap path). With moves,
 * the overlay covers the union of every position the widget can occupy, and the
 * writer composes each state's image at its offset inside that box — position
 * changes ride the same file-swap channel the images already use, because
 * overlay's x/y are baked into the filter graph and cannot change on air.
 */
export function symbolBounds(
  widget: OverlayWidget
): { x: number; y: number; w: number; h: number } | null {
  if (widget.type !== 'symbol') return null
  const moved = widget.conditions.filter(conditionMoved)
  if (moved.length === 0) return null
  let x0 = widget.x
  let y0 = widget.y
  let x1 = widget.x + widget.w
  let y1 = widget.y + widget.h
  for (const c of moved) {
    const p = conditionPlace(widget, c)
    x0 = Math.min(x0, p.x)
    y0 = Math.min(y0, p.y)
    x1 = Math.max(x1, p.x + widget.w)
    y1 = Math.max(y1, p.y + widget.h)
  }
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }
}

/** A symbol's full current state: which image, and where the box sits. */
export function symbolState(
  raw: string | null,
  widget: OverlayWidget
): { src: string | null; x: number; y: number } {
  const mapped = matchedCondition(raw, widget)
  const src = (mapped?.text ?? '').trim() || widget.symbolDefault.trim() || null
  const p = mapped ? conditionPlace(widget, mapped) : { x: widget.x, y: widget.y }
  return { src, x: p.x, y: p.y }
}

export function drawableWidgets(overlay: OverlayConfig): OverlayWidget[] {
  return overlay.widgets
    .filter((w) => w.enabled && isDrawable(w.type))
    .slice(0, MAX_OVERLAY_WIDGETS)
    .sort((a, b) => a.z - b.z)
}

/**
 * Whether this build knows how to draw the type.
 *
 * A config can legitimately carry a type this build has never heard of — the
 * fleet console pushes one design to devices on different versions, and the
 * config round-trips through them. Such a widget is preserved on disk and
 * skipped here rather than being treated as corrupt.
 */
export function isDrawable(type: string): type is OverlayWidgetType {
  return (
    type === 'value' ||
    type === 'label' ||
    type === 'bar' ||
    type === 'lamp' ||
    type === 'trend'
  )
}

/**
 * The list SmartLinkOS keeps alongside each metric.
 *
 * `redis_client.set_metric` writes the current value to `metric:{ns}:{name}` and
 * LPUSHes the same sample onto `history:{ns}:{name}`, trimmed to the last 30. So
 * the trend needs no buffer of its own — and unlike one, this survives the app
 * being restarted, because the plant kept the history, not us.
 */
export function historyKey(tag: string): string | null {
  const clean = tag.trim()
  return clean.startsWith('metric:') ? 'history:' + clean.slice('metric:'.length) : null
}

/** Newest-first, as LRANGE returns it; the caller gets chronological order. */
const SPARK_BARS = ['\u2581', '\u2582', '\u2583', '\u2584', '\u2585', '\u2586', '\u2587', '\u2588']
const SPARK_ASCII = ['_', '.', '-', '~', '+', '=', '*', '#']

/**
 * A sparkline drawn as text.
 *
 * The same trick the bar uses, one column per sample: eight glyph heights give a
 * readable shape at a glance, which is what a trend on a broadcast is for. It is
 * not a pixel-accurate plot and cannot be — a filter graph has no way to draw a
 * polyline from data — but it answers "is it climbing?" from across a room.
 *
 * `trendAuto` rescales to the window's own range, which is what makes a flat-ish
 * signal legible; the fixed min/max is there for when absolute level matters
 * more than shape.
 */
export function trendText(history: string[] | undefined, widget: OverlayWidget): string {
  const ramp = widget.trendGlyph === 'ascii' ? SPARK_ASCII : SPARK_BARS
  const cells = Math.min(30, Math.max(2, Math.round(widget.trendCells)))
  const nums = (history ?? [])
    .map((v) => Number.parseFloat(sanitizeValue(v)))
    .filter((n) => Number.isFinite(n))
    .slice(0, cells)
    .reverse()
  if (nums.length === 0) return ramp[0].repeat(cells)

  let lo = widget.min
  let hi = widget.max
  if (widget.trendAuto) {
    lo = Math.min(...nums)
    hi = Math.max(...nums)
  }
  // A flat series has no range to scale against; draw it on the baseline rather
  // than dividing by zero and painting a full-height block.
  const span = hi - lo
  const level = (n: number): string => {
    if (span <= 0) return ramp[0]
    const at = Math.round(((n - lo) / span) * (ramp.length - 1))
    return ramp[Math.min(ramp.length - 1, Math.max(0, at))]
  }
  return nums.map(level).join('')
}
